import { banqoraFileExtractionApi } from '~/api/def/servers/defaultServer';
import { getExecEndpoint } from '~/api/service/getExecEndpoint';

export const portfolioDocumentUploadEndpoint = {
  server: banqoraFileExtractionApi,
  path: 'upload',
  description: 'Upload portfolio document and extract data',
};

export interface PortfolioDocumentUploadResponse {
  success: boolean;
  document_id: string;
  doc_uuid: string;
  s3_url: string;
  extracted_data: string;
  presigned_url?: string;
}

export interface PortfolioDocumentUploadEndpointDef {
  endpoint: typeof portfolioDocumentUploadEndpoint;
  methods: {
    POST: {
      response: PortfolioDocumentUploadResponse;
      payload: FormData;
    };
  };
}

const endpointPortfolioDocumentUploadExec =
  getExecEndpoint<PortfolioDocumentUploadEndpointDef>(
    portfolioDocumentUploadEndpoint,
  );

export const endpointPortfolioDocumentUploadPOST = async (
  portfolioId: string,
  file: File,
): Promise<PortfolioDocumentUploadResponse> => {
  const formData = new FormData();
  formData.append('file', file);

  // Don't set Content-Type header - axios will set it automatically with the correct boundary
  const result = await endpointPortfolioDocumentUploadExec<'POST'>({
    method: 'POST',
    payload: formData,
    urlExtra: `/${portfolioId}`,
  });

  if (!result) {
    throw new Error('Failed to upload document');
  }

  return result;
};

export interface PortfolioDocumentPresignedUrl {
  success: boolean;
  portfolio_id: string;
  doc_uuid: string;
  document_name: string;
  document_type: string;
  presigned_url: string;
  created_at: string;
  expires_in: number;
}

export interface PortfolioDocumentsPresignedUrlsResponse {
  success: boolean;
  portfolio_id?: string;
  doc_uuid?: string;
  document_name?: string;
  document_type?: string;
  presigned_url?: string;
  created_at?: string;
  expires_in?: number;
}

// Separate endpoint for fetching presigned URLs (different path structure)
export const portfolioDocumentsPresignedUrlsEndpoint = {
  server: banqoraFileExtractionApi,
  path: 'portfolio',
  description: 'Get portfolio documents presigned URLs',
};

export interface PortfolioDocumentsPresignedUrlsEndpointDef {
  endpoint: typeof portfolioDocumentsPresignedUrlsEndpoint;
  methods: {
    GET: {
      response: PortfolioDocumentsPresignedUrlsResponse;
    };
  };
}

const endpointPortfolioDocumentsPresignedUrlsExec =
  getExecEndpoint<PortfolioDocumentsPresignedUrlsEndpointDef>(
    portfolioDocumentsPresignedUrlsEndpoint,
  );

export const endpointPortfolioDocumentsPresignedUrlsGET = async (
  portfolioId: string,
): Promise<PortfolioDocumentsPresignedUrlsResponse> => {
  const result = await endpointPortfolioDocumentsPresignedUrlsExec<'GET'>({
    method: 'GET',
    urlExtra: `/${portfolioId}/document/presigned-url`,
  });

  if (!result) {
    throw new Error('Failed to fetch document presigned URLs');
  }

  return result;
};

// Delete document endpoint
export const portfolioDocumentDeleteEndpoint = {
  server: banqoraFileExtractionApi,
  path: 'document',
  description: 'Delete portfolio document',
};

export interface PortfolioDocumentDeleteResponse {
  success: boolean;
  doc_uuid: string;
  deleted_from_s3: boolean;
}

export interface PortfolioDocumentDeleteEndpointDef {
  endpoint: typeof portfolioDocumentDeleteEndpoint;
  methods: {
    DELETE: {
      response: PortfolioDocumentDeleteResponse;
    };
  };
}

const endpointPortfolioDocumentDeleteExec =
  getExecEndpoint<PortfolioDocumentDeleteEndpointDef>(
    portfolioDocumentDeleteEndpoint,
  );

export const endpointPortfolioDocumentDelete = async (
  docUuid: string,
): Promise<PortfolioDocumentDeleteResponse> => {
  const result = await endpointPortfolioDocumentDeleteExec<'DELETE'>({
    method: 'DELETE',
    urlExtra: `/${docUuid}`,
  });

  if (!result) {
    throw new Error('Failed to delete document');
  }

  return result;
};

// Extract document data endpoint
export const portfolioDocumentExtractionEndpoint = {
  server: banqoraFileExtractionApi,
  path: 'document',
  description: 'Extract data from portfolio document',
};

export interface PortfolioDocumentExtractionResponse {
  success: boolean;
  doc_uuid: string;
  document_name: string;
  document_type: string;
  extracted_data: string;
}

export interface PortfolioDocumentExtractionEndpointDef {
  endpoint: typeof portfolioDocumentExtractionEndpoint;
  methods: {
    GET: {
      response: PortfolioDocumentExtractionResponse;
    };
  };
}

const endpointPortfolioDocumentExtractionExec =
  getExecEndpoint<PortfolioDocumentExtractionEndpointDef>(
    portfolioDocumentExtractionEndpoint,
  );

export const endpointPortfolioDocumentExtractionGET = async (
  docUuid: string,
): Promise<PortfolioDocumentExtractionResponse> => {
  const result = await endpointPortfolioDocumentExtractionExec<'GET'>({
    method: 'GET',
    urlExtra: `/${docUuid}/extraction`,
  });

  if (!result) {
    throw new Error('Failed to extract document data');
  }

  return result;
};
